import { ReactElement, ReactNode } from 'react'
import { formatCents, formatShare } from '../format'
import { GroupTable, GroupTableColumn } from '../GroupTable'
import { occupancyTone, ProgressBar } from '../ProgressBar'
import * as S from './styles'
import { SummaryCellData, SummaryColumns, SummarySort, SummarySortKey, SummaryValues } from './types'

export type { SummaryCellData, SummaryColumns, SummarySort, SummarySortKey, SummaryValues } from './types'

interface ColumnDefinition extends GroupTableColumn<SummaryValues> {
  id: SummarySortKey
  visible: (columns: SummaryColumns) => boolean
}

export interface SummaryTableGroup {
  key: string
  label: string
  values: SummaryValues
  expanded: boolean
  onToggle: () => void
  detail: ReactNode
}

interface SummaryTableProps {
  groupLabel: string
  columns: SummaryColumns
  groups: SummaryTableGroup[]
  total: { label: string, values: SummaryValues }
  sort: SummarySort | null
  onSort: (key: SummarySortKey) => void
  allExpanded: boolean
  onToggleAll: () => void
  detailMinWidth?: number
}

const formatCount = (value: number): string => value.toLocaleString('pt-BR')

const cell = (data: SummaryCellData): ReactNode => {
  if (data.low) return <S.Low title={data.hint}>{data.text}</S.Low>
  if (data.value === null && data.hint !== undefined && data.text === '—') {
    return (
      <S.Muted title={data.hint}>
        <span aria-hidden="true">—</span>
        <S.Hidden>{data.hint}</S.Hidden>
      </S.Muted>
    )
  }
  if (data.value === null) return <S.Muted title={data.hint}>{data.text}</S.Muted>
  return <span title={data.hint}>{data.text}</span>
}

const occupancy = (percent: number | null): ReactNode => {
  if (percent === null) return <S.Muted>—</S.Muted>
  const text = `${formatCount(percent)}%`
  return (
    <S.Occupancy>
      <ProgressBar percentage={percent} track="neutral" tone={occupancyTone(percent)} label={`Ocupação ${text}`} />
      <S.OccupancyText>{text}</S.OccupancyText>
    </S.Occupancy>
  )
}

const DATA_COLUMNS: ColumnDefinition[] = [
  { id: 'capacity', label: 'Capacidade', width: 'minmax(88px, 1fr)', minWidth: 88, align: 'right', visible: (c) => c.stock, render: (v) => cell(v.capacity) },
  { id: 'sold', label: 'Vendidos', width: 'minmax(84px, 1fr)', minWidth: 84, align: 'right', visible: () => true, render: (v) => formatCount(v.sold) },
  { id: 'courtesies', label: 'Cortesias', width: 'minmax(84px, 1fr)', minWidth: 84, align: 'right', visible: () => true, render: (v) => formatCount(v.courtesies) },
  { id: 'held', label: 'Reservados', width: 'minmax(92px, 1fr)', minWidth: 92, align: 'right', visible: (c) => c.held, render: (v) => cell(v.held) },
  { id: 'available', label: 'Disponível', width: 'minmax(92px, 1fr)', minWidth: 92, align: 'right', visible: (c) => c.stock, render: (v) => cell(v.available) },
  { id: 'occupancy', label: 'Ocupação', width: 'minmax(112px, 1.3fr)', minWidth: 112, align: 'left', visible: (c) => c.stock, render: (v) => occupancy(v.occupancy) },
  { id: 'revenue', label: 'Receita', width: 'minmax(128px, 1.3fr)', minWidth: 128, align: 'right', visible: () => true, render: (v) => formatCents(v.revenueCents) },
  { id: 'share', label: '% receita', width: 'minmax(80px, 0.9fr)', minWidth: 80, align: 'right', visible: () => true, render: (v) => (v.revenueShare === null ? <S.Muted>—</S.Muted> : formatShare(v.revenueShare)) },
  { id: 'fee', label: 'Taxa', width: 'minmax(104px, 1.1fr)', minWidth: 104, align: 'right', visible: (c) => c.fee, render: (v) => formatCents(v.feeCents ?? 0) },
  { id: 'average', label: 'Ticket médio', width: 'minmax(104px, 1.1fr)', minWidth: 104, align: 'right', visible: () => true, render: (v) => (v.averageTicketCents === null ? <S.Muted>—</S.Muted> : formatCents(v.averageTicketCents)) },
]

export function SummaryTable({
  groupLabel,
  columns,
  groups,
  total,
  sort,
  onSort,
  allExpanded,
  onToggleAll,
  detailMinWidth = 0,
}: SummaryTableProps): ReactElement {
  return (
    <GroupTable<SummaryValues>
      groupLabel={groupLabel}
      columns={DATA_COLUMNS.filter((column) => column.visible(columns))}
      groups={groups}
      total={total}
      sort={sort}
      onSort={(key) => onSort(key as SummarySortKey)}
      allExpanded={allExpanded}
      onToggleAll={onToggleAll}
      detailMinWidth={detailMinWidth}
    />
  )
}
